/**
 * Configurator step UI — extracted from App.tsx 2388-3127.
 *
 * The configurator owns the step navigation, option selection, and
 * summary/quote rendering. State is passed in from `App`; this component
 * is presentation + the small per-step interactions.
 */

import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  ChevronRight,
  ChevronLeft,
  Check,
  Upload,
  AlertCircle,
  Loader2,
  Package,
  User,
  Image as ImageIcon,
  ShoppingBag,
  Trash2,
  ShoppingCart as CartIcon,
} from 'lucide-react';
import { formatMoney, stripBoxSuffix } from '@brandbox/content-schema';
import type {
  Cart,
  FlowConfig,
  LogoSelection,
  Option,
  Selections,
  Step,
  CustomerInfo,
} from '../types';
import {
  computeUnitBoxPrice,
  describeBand,
  getActiveTier,
  getLowestBandPrice,
  getTieredPrice,
  hasEffectiveTierPrices,
} from '../lib/pricing';
import type { DefaultsSurface } from '../lib/content';
import {
  getRailSteps,
  getStepCategories,
  isOptionsStep,
  stepHasCategoryTabs,
} from '../lib/flowSteps';
import { getMaxSelections, isOtherOption } from '../lib/orderPipeline';
import { ImageWithSkeleton } from './ImageWithSkeleton';
import { QuantityStepper } from './QuantityStepper';
import { SiteFooter } from './SiteFooter';

interface Props {
  flow: FlowConfig;
  cart: Cart;
  defaults: DefaultsSurface;
  currentStep: Step;
  currentStepIndex: number;
  selections: Selections;
  customerInfo: CustomerInfo;
  setCustomerInfo: (info: CustomerInfo) => void;
  selectedCategory: string | null;
  /**
   * N6b — the admin-driven box header name + image, computed once in the
   * configurator hook via `resolveBoxName` (category label → flow title →
   * humanized id) as `"<NAME> BOX"`. The header renders THIS instead of
   * re-deriving the name from the raw `selectedCategory` id, so the box name
   * shown in the configurator chrome is the same admin-controlled value as
   * the landing tile / cart heading. `null`/absent before a box is selected
   * (the production caller always passes it; optional only so chrome-only
   * test renders can omit it and fall back to the humanized id).
   */
  selectedBox?: { name: string; image: string } | null;
  /**
   * Chapter 10 (F15) — every logo the customer attached, in pick order. A
   * company can have more than one, so this is a list; the upload step shows
   * the first one's preview and the summary names all of them.
   */
  logos: readonly LogoSelection[];
  isSubmitting: boolean;
  error: string | null;
  /**
   * Phase N2b — generic per-step "Other…" free-text, keyed by step id. The
   * configurator renders an Other input on ANY step that has an `isOther`
   * option (any number of slots, any position), replacing the three fixed
   * packaging/merch/finalTouch slots.
   */
  otherValues: Record<string, string>;
  setStepOther: (stepId: string, value: string) => void;
  /** @deprecated N2b — superseded by `otherValues`/`setStepOther`. Unused. */
  packagingOther?: string;
  /** @deprecated N2b */
  setPackagingOther?: (v: string) => void;
  /** @deprecated N2b */
  merchOther?: string;
  /** @deprecated N2b */
  setMerchOther?: (v: string) => void;
  /** @deprecated N2b */
  finalTouchOther?: string;
  /** @deprecated N2b */
  setFinalTouchOther?: (v: string) => void;
  qtyInputRef: React.MutableRefObject<HTMLInputElement | null>;

  // actions
  prevStep: () => void;
  nextStep: () => void;
  goToStep: (stepId: string) => void;
  toggleOption: (optionId: string) => void;
  updateQuantity: (q: number) => void;
  removeItem: (id: string) => void;
  handleLogoUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handlePricingImport: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleSubmit: (agreedToNewsletter: boolean) => void | Promise<void>;
  checkDependencies: (step: Step) => boolean;
  isStepValid: () => boolean;
  setIsCartOpen: (open: boolean) => void;
}

/**
 * The option card's price line.
 *
 * Chapter 10 (F4): this used to read the `250+` band directly and print the raw
 * key — `From €3,50 (250+)`. Two problems. The key now denotes 250-499, so the
 * label told the customer that 250 and 2,500 were one band; and "from" was only
 * accidentally right while `250+` was the top band — pricing `1000+` lower would
 * have made the advertised floor an overstatement. It now takes the cheapest
 * AUTHORED band and names it through `describeBand`.
 */
function fromPriceLabel(option: Option): string {
  if (option.price === undefined || option.price === 0) return 'Custom';
  const lowest = getLowestBandPrice(option);
  const money = formatMoney(lowest?.price ?? option.price);
  return lowest === null ? `From ${money}` : `From ${money} (${describeBand(lowest.tier)})`;
}

export function ConfiguratorView(props: Props) {
  const {
    flow,
    cart,
    defaults,
    currentStep,
    selections,
    customerInfo,
    setCustomerInfo,
    selectedCategory,
    selectedBox,
    logos,
    isSubmitting,
    error,
    otherValues,
    setStepOther,
    qtyInputRef,
    prevStep,
    nextStep,
    goToStep,
    toggleOption,
    updateQuantity,
    removeItem,
    handleLogoUpload,
    handlePricingImport,
    handleSubmit,
    checkDependencies,
    isStepValid,
    setIsCartOpen,
  } = props;

  // Phase N1a — the category tabs (and their active-tab state) are derived
  // from the current step's distinct option categories, not the literal
  // 'merch' id. For Spring/Office the merch step's categories are
  // [Essentials, Gadgets, Food & Beverages, Textile], so the default tab
  // is 'Essentials' exactly as before.
  const stepCategories = getStepCategories(currentStep);
  const hasCategoryTabs = stepHasCategoryTabs(currentStep);
  // Position of the current step among the option-type steps (0-based).
  // Used to key the per-step "Other…" free-text slot generically.
  const railSteps = getRailSteps(flow);
  const optionStepIndex = railSteps.findIndex((s) => s.id === currentStep.id);
  // The last option step is the one whose "Next" button reads "Review
  // Quote" and advances to checkout (final-touch for Spring/Office).
  const isLastOptionStep =
    optionStepIndex !== -1 && optionStepIndex === railSteps.length - 1;
  const [activeMerchTab, setActiveMerchTab] = useState(stepCategories[0] ?? 'Essentials');
  // Chapter 10 (F6) — the rail's visible heading doubles as the accessible
  // name of the pill group, so sighted and screen-reader users are told the
  // same thing about what the control is.
  const categoryRailLabelId = React.useId();

  // Redesign pass B (item 8) — the disappearing 4-second "Tip: you can also
  // browse…" coach-mark is gone. The segmented category control below is
  // permanent and carries live item counts, so it explains itself.
  useEffect(() => {
    if (hasCategoryTabs) {
      setActiveMerchTab(stepCategories[0] ?? 'Essentials');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep.id]);

  // Redesign pass B (item 9) — honest progress. The journey the customer
  // actually walks is every option step in the rail, then the quote page
  // (quantity, logo upload, order details, contact, review — all live on
  // the checkout). Flow-doc steps the journey skips (the legacy form/summary
  // steps; `nextStep` jumps from the last option step straight to checkout)
  // no longer dilute the bar, so the bar and the rail labels agree.
  const railIndex = railSteps.findIndex((s) => s.id === currentStep.id);
  const journeyLength = railSteps.length + 1; // + the "your quote" stage
  // Non-rail steps (form/upload/summary, reachable only via deep links) sit
  // on the quote side of the journey.
  const journeyIndex = railIndex === -1 ? railSteps.length : railIndex;
  const isQuoteStage = railIndex === -1;
  const progress = ((journeyIndex + 1) / journeyLength) * 100;

  // N6b — the admin-driven box name for the configurator header. The hook
  // resolves `selectedBox.name` as `"<NAME> BOX"` via `resolveBoxName`
  // (category label → flow title → humanized id). We drop the trailing
  // " BOX" so the green "BOX" word can keep its own styled span. When no box
  // is selected yet (header not shown), fall back to the humanized id so the
  // chrome is never blank.
  // F22 — the strip is `stripBoxSuffix`, the same owner that decides whether
  // the suffix belongs, so the header and the name can't disagree.
  const boxHeaderName = selectedBox
    ? stripBoxSuffix(selectedBox.name)
    : selectedCategory?.replace('-', ' ') ?? '';

  // Redesign pass A — the floating cart badge shows the live number of
  // items in the box (packaging counts as 1, plus every merch and final
  // touch selection), replacing the old hard-coded "1".
  const cartItemCount =
    (cart.box.packagingId ? 1 : 0) +
    cart.box.merchIds.length +
    cart.box.finalTouchIds.length;

  return (
    <div className="min-h-screen font-sans relative overflow-hidden">
      {/* Redesign pass C (item 17) — no background spheres in the builder;
          the gradient-sphere motif is reserved for the landing hero and the
          success screen. */}
      {selectedCategory && (
        <div className="fixed top-6 right-6 z-[60]">
          <button
            onClick={() => setIsCartOpen(true)}
            className="relative p-4 bg-white rounded-full shadow-lg border border-black/5 hover:scale-110 transition-all group"
            aria-label={`Open shopping cart, ${cartItemCount} item${cartItemCount === 1 ? '' : 's'}`}
          >
            <CartIcon className="w-6 h-6 text-[#356d4e]" />
            {cartItemCount > 0 && (
              <span
                data-testid="cart-count-badge"
                className="absolute -top-1 -right-1 min-w-6 h-6 px-1 bg-[#57c858] text-white text-[11px] font-bold rounded-full flex items-center justify-center border-2 border-white"
              >
                {cartItemCount}
              </span>
            )}
          </button>
        </div>
      )}

      <header className="relative z-20 sticky top-0 bg-white/80 backdrop-blur-md border-b border-gray-100 w-full">
        <div className="h-1.5 bg-gray-100 w-full overflow-hidden">
          <motion.div
            className="h-full bg-linear-to-r from-[#356d4e] to-[#57c858]"
            initial={{ width: 0 }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.5 }}
          />
        </div>

        <div className="w-full max-w-[95%] mx-auto py-3 px-4">
          <div className="flex items-center justify-between gap-4">
            <button
              onClick={prevStep}
              className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-gray-50 border border-gray-200 text-[#444444] font-century text-xs md:text-sm font-black tracking-[0.15em] hover:bg-white hover:border-[#57c858] hover:text-[#356d4e] transition-all shadow-sm group"
              aria-label="Go back to previous step"
            >
              <ChevronLeft className="w-3.5 h-3.5 md:w-4 md:h-4 group-hover:-translate-x-0.5 transition-transform" />
              <span>BACK</span>
            </button>

            <div className="flex items-baseline gap-1.5">
              <span className="text-lg md:text-2xl font-bold font-century uppercase text-[#444444] tracking-tight">
                {/*
                 * N6b — the box NAME is the admin-driven value resolved in the
                 * configurator hook (category label → flow title → humanized
                 * id) as `"<NAME> BOX"`. We strip the trailing " BOX" here
                 * because the green "BOX" word is rendered as its own span
                 * below. For Spring/Office this is byte-identical to the old
                 * `selectedCategory.replace('-', ' ')` (uppercased by CSS):
                 * "SPRING" / "OFFICE". The fallback to the raw id only fires
                 * before a box is selected (header isn't shown then anyway).
                 */}
                {boxHeaderName}
              </span>
              <span
                aria-hidden="true"
                className="w-1.5 h-1.5 rounded-full bg-linear-to-br from-[#57c858] to-[#356d4e] inline-block"
              />
              <span className="text-lg md:text-2xl font-bold font-century uppercase text-[#356d4e] tracking-tight">
                BOX
              </span>
            </div>

            <div className="hidden md:block w-24" />
          </div>
        </div>
      </header>

      <main className="relative z-10 w-full max-w-[90%] mx-auto py-8 pb-48">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="space-y-12"
          >
            <div className="w-full mb-12 pb-8">
              <div className="flex items-center justify-center gap-x-4 sm:gap-x-10 flex-nowrap whitespace-nowrap overflow-hidden">
                {getRailSteps(flow)
                  .map((s, i) => ({ id: s.id, label: `${i + 1}. ${s.title.toUpperCase()}` }))
                  .map((step, index, array) => {
                  const isActive = currentStep.id === step.id;
                  return (
                    <React.Fragment key={step.id}>
                      <button
                        onClick={() => {
                          goToStep(step.id);
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }}
                        className={`flex items-baseline gap-2 group/step cursor-pointer transition-all duration-300 ${
                          isActive ? 'opacity-100' : 'opacity-40 hover:opacity-100'
                        }`}
                      >
                        <span
                          className={`font-century font-bold uppercase tracking-tight transition-all duration-300 ${
                            isActive
                              ? 'text-[clamp(1rem,4.5vw,2.25rem)] text-[#356d4e]'
                              : 'text-[clamp(0.75rem,3vw,1.25rem)] text-[#444444]'
                          } group-hover/step:underline underline-offset-8`}
                        >
                          {step.label}
                        </span>
                        <span
                          aria-hidden="true"
                          className={`gradient-dot transition-all duration-300 ${
                            isActive
                              ? 'w-2.5 h-2.5 sm:w-4 sm:h-4'
                              : 'w-1.5 h-1.5 sm:w-2 sm:h-2'
                          }`}
                        />
                      </button>
                      <span className="text-gray-300 font-century text-[clamp(0.75rem,3vw,1.25rem)] self-center mb-1">
                        →
                      </span>
                    </React.Fragment>
                  );
                })}
                {/* Redesign pass B (item 9) — the journey's real last stage.
                    Quantity, logo, details and review all live on the quote
                    page, reached via "Review Quote"; naming it here keeps the
                    rail honest about what's left. Not a deep link — the quote
                    gate (packaging + merch chosen) stays with the CTA. */}
                <div
                  aria-current={isQuoteStage ? 'step' : undefined}
                  className={`flex items-baseline gap-2 transition-all duration-300 ${
                    isQuoteStage ? 'opacity-100' : 'opacity-40'
                  }`}
                >
                  <span
                    className={`font-century font-bold uppercase tracking-tight transition-all duration-300 ${
                      isQuoteStage
                        ? 'text-[clamp(1rem,4.5vw,2.25rem)] text-[#356d4e]'
                        : 'text-[clamp(0.75rem,3vw,1.25rem)] text-[#444444]'
                    }`}
                  >
                    {railSteps.length + 1}. YOUR QUOTE
                  </span>
                  <span
                    aria-hidden="true"
                    className={`gradient-dot transition-all duration-300 ${
                      isQuoteStage ? 'w-2.5 h-2.5 sm:w-4 sm:h-4' : 'w-1.5 h-1.5 sm:w-2 sm:h-2'
                    }`}
                  />
                </div>
              </div>
            </div>

            <div className="mt-8">
              {(!currentStep.type || currentStep.type === 'options') && (
                <div
                  data-testid="options-grid"
                  className="grid grid-cols-1 lg:grid-cols-5 gap-x-8 gap-y-12"
                >
                  {hasCategoryTabs && (
                    /* Redesign pass B (item 8) — permanent segmented category
                       control with live item counts, derived from the step's
                       own option categories.
                     *
                     * Chapter 10 (F6) — all three testers missed this control.
                     * It was already a vertical column, so "make it vertical"
                     * was not the fix; three things were wrong:
                     *   - nothing NAMED it, so four outline pills in a bare
                     *     left column read as decoration. Hence the heading and
                     *     the `aria-labelledby` group.
                     *   - the column was reserved from `md` up at one fifth of
                     *     the grid — ~110px, at which the tablet baseline shows
                     *     labels and counts spilling outside the pill border.
                     *     It now claims a column only from `lg`.
                     *   - below that breakpoint the same column went full
                     *     width, so four tall pills pushed the products off the
                     *     first screen. Below `lg` it is a compact
                     *     horizontally-scrolling row instead.
                     * Sticky on `lg` so the rail is still there after the
                     * customer scrolls into the second row of products —
                     * scrolling past it is how it got missed. */
                    <div
                      data-testid="category-rail"
                      className="col-span-full lg:col-span-1 lg:sticky lg:top-28 lg:self-start space-y-3"
                    >
                      <p
                        id={categoryRailLabelId}
                        className="px-1 text-[11px] font-black uppercase tracking-[0.2em] text-gray-400"
                      >
                        Categories
                      </p>
                      <div
                        role="group"
                        aria-labelledby={categoryRailLabelId}
                        className="-mx-1 flex gap-3 overflow-x-auto px-1 pb-2 lg:mx-0 lg:flex-col lg:gap-4 lg:overflow-visible lg:px-0 lg:pb-0"
                      >
                        {stepCategories.map((cat) => {
                          const isTabActive = activeMerchTab === cat;
                          const itemCount = (currentStep.options ?? []).filter(
                            (o) => o.category === cat,
                          ).length;
                          const selectedCount = (selections[currentStep.id] || []).filter(
                            (id) =>
                              currentStep.options?.find((o) => o.id === id)?.category === cat,
                          ).length;
                          return (
                            <button
                              key={cat}
                              onClick={() => setActiveMerchTab(cat)}
                              aria-pressed={isTabActive}
                              className={`flex shrink-0 items-center justify-between gap-3 whitespace-nowrap rounded-full border px-6 py-3 text-left font-century text-sm uppercase tracking-widest transition-all lg:w-full ${
                                isTabActive
                                  ? 'border-[#356d4e] border-[3px] bg-[#57c858]/15 text-[#444444]'
                                  : 'border-[#356d4e] bg-transparent text-[#444444] hover:bg-[#57c858]/5'
                              }`}
                            >
                              <span>{cat}</span>
                              <span className="flex items-center gap-1.5 flex-shrink-0">
                                {selectedCount > 0 && (
                                  <span
                                    aria-label={`${selectedCount} selected`}
                                    className="min-w-5 h-5 px-1 rounded-full bg-[#57c858] text-white text-[11px] font-bold flex items-center justify-center"
                                  >
                                    {selectedCount}
                                  </span>
                                )}
                                <span
                                  aria-label={`${itemCount} ${itemCount === 1 ? 'item' : 'items'}`}
                                  className="text-xs font-bold text-gray-400 tracking-normal"
                                >
                                  {itemCount}
                                </span>
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  <div
                    className={`col-span-full ${
                      hasCategoryTabs ? 'lg:col-span-4' : ''
                    } grid grid-cols-2 ${
                      hasCategoryTabs ? 'lg:grid-cols-4' : 'lg:grid-cols-5'
                    } gap-x-8 gap-y-12`}
                  >
                    <AnimatePresence mode="wait">
                      <motion.div
                        key={hasCategoryTabs ? activeMerchTab : currentStep.id}
                        initial={{ opacity: 0, x: 10 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -10 }}
                        transition={{ duration: 0.3 }}
                        className={`col-span-full grid grid-cols-2 ${
                          hasCategoryTabs ? 'lg:grid-cols-4' : 'lg:grid-cols-5'
                        } gap-x-8 gap-y-12`}
                      >
                        {currentStep.options
                          ?.filter(
                            // Show only the active category's options when this
                            // step has tabs; otherwise show every option.
                            (option) => !hasCategoryTabs || option.category === activeMerchTab,
                          )
                          .map((option) => {
                            const isSelected = (selections[currentStep.id] || []).includes(
                              option.id,
                            );
                            // N2-closeout — read the ONE cap convention
                            // (`getMaxSelections`: 0/absent = unlimited; 1 =
                            // single; N = cap), the SAME source the handler
                            // (`toggleOption`) and the modals read. Previously
                            // the badge recomputed it as `maxSelections || 1`,
                            // a second parallel definition that could disagree
                            // with the handler.
                            const max = getMaxSelections(currentStep);

                            // "Other…" detection is owned by `isOtherOption`
                            // (first-class flag + legacy id convention), so
                            // every consumer decides identically.
                            const isOther = isOtherOption(option);
                            const isUnavailable = !!(
                              option.minBoxes && cart.qty < option.minBoxes
                            );

                            // The free-text "Other…" value is keyed generically
                            // by the step's id (not position 0/1/2), so any
                            // step's Other slot round-trips into cart →
                            // submission → email.
                            const otherValue = otherValues[currentStep.id] ?? '';
                            const setOtherValue = (v: string) =>
                              setStepOther(currentStep.id, v);

                            return (
                              <div key={option.id} className="flex flex-col gap-4">
                                <motion.button
                                  whileHover={!isUnavailable ? { scale: 1.01 } : {}}
                                  whileTap={!isUnavailable ? { scale: 0.99 } : {}}
                                  onClick={() => !isUnavailable && toggleOption(option.id)}
                                  disabled={isUnavailable}
                                  className={`relative group flex flex-col items-start transition-all text-left bg-transparent p-0 border-none outline-none w-full ${
                                    isUnavailable
                                      ? 'opacity-50 cursor-not-allowed'
                                      : 'cursor-pointer'
                                  }`}
                                  aria-selected={isSelected}
                                  role="radio"
                                >
                                  {option.image && (
                                    <div
                                      className={`relative w-full aspect-[9/11] rounded-2xl overflow-hidden mb-6 transition-all ${
                                        isSelected
                                          ? 'ring-2 ring-[#57c858] shadow-lg shadow-[#57c858]/20'
                                          : 'group-hover:ring-2 group-hover:ring-gray-200'
                                      }`}
                                    >
                                      <ImageWithSkeleton
                                        src={option.image}
                                        alt={`${currentStep.id} option: ${option.label}`}
                                        width={400}
                                        height={500}
                                        loading="lazy"
                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                        referrerPolicy="no-referrer"
                                      />
                                      {isUnavailable && (
                                        <div className="absolute inset-0 bg-black/40 flex items-center justify-center p-4 text-center">
                                          <div className="bg-white/90 backdrop-blur-sm p-2 rounded-lg shadow-xl">
                                            <p className="text-[13px] font-black text-[#356d4e]">
                                              Unavailable below {option.minBoxes} boxes
                                            </p>
                                          </div>
                                        </div>
                                      )}
                                      {isSelected && (
                                        <div className="absolute top-3 right-3 w-7 h-7 rounded-full bg-[#57c858] flex items-center justify-center shadow-md z-10">
                                          {max > 1 ? (
                                            <span className="text-white text-xs font-bold">
                                              {(selections[currentStep.id] || []).indexOf(
                                                option.id,
                                              ) + 1}
                                            </span>
                                          ) : (
                                            <Check className="w-4 h-4 text-white stroke-[3px]" />
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  )}

                                  <div className="w-full space-y-1">
                                    <p className="font-bold text-lg text-[#444444] uppercase">
                                      {option.label}
                                    </p>

                                    <p className="font-bold text-sm text-[#444444]">
                                      {option.priceLabel || fromPriceLabel(option)}
                                    </p>
                                    {hasEffectiveTierPrices(option) && (
                                      <p className="text-xs text-gray-500 font-bold">
                                        Price depends on quantity
                                      </p>
                                    )}

                                    {option.description && (
                                      <p className="text-sm text-[#444444] leading-relaxed opacity-70">
                                        {option.description}
                                      </p>
                                    )}
                                  </div>
                                </motion.button>

                                {isOther && isSelected && (
                                  <motion.div
                                    initial={{ opacity: 0, height: 0 }}
                                    animate={{ opacity: 1, height: 'auto' }}
                                    className="w-full"
                                  >
                                    <input
                                      type="text"
                                      placeholder="Tell us what you want..."
                                      value={otherValue}
                                      onChange={(e) => setOtherValue(e.target.value)}
                                      className="w-full p-4 bg-white border-2 border-[#57c858] rounded-xl outline-none font-century text-sm tracking-wider"
                                      autoFocus
                                    />
                                  </motion.div>
                                )}
                              </div>
                            );
                          })}
                      </motion.div>
                    </AnimatePresence>
                  </div>
                </div>
              )}

              {currentStep.type === 'upload' && (
                <div className="max-w-[90%] mx-auto">
                  <label className="flex flex-col items-center justify-center w-full h-64 border-2 border-dashed border-gray-300 rounded-3xl cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors">
                    <div className="flex flex-col items-center justify-center pt-5 pb-6">
                      {logos[0]?.preview ? (
                        <img
                          src={logos[0].preview}
                          alt="Preview"
                          className="h-32 object-contain mb-4"
                        />
                      ) : (
                        <Upload className="w-12 h-12 text-gray-400 mb-4" />
                      )}
                      <p className="mb-2 text-sm text-gray-500">
                        <span className="font-semibold">Click to upload</span> or drag your logo
                        here
                      </p>
                      <p className="text-xs text-gray-400">
                        PNG, JPG or SVG (Max. {currentStep.maxFileSizeMB}MB)
                      </p>
                    </div>
                    <input
                      type="file"
                      className="hidden"
                      accept={currentStep.accept}
                      onChange={handleLogoUpload}
                    />
                  </label>
                  {error && (
                    <div className="mt-4 p-4 bg-red-50 text-red-700 rounded-xl flex items-center gap-2">
                      <AlertCircle className="w-5 h-5" />
                      {error}
                    </div>
                  )}
                </div>
              )}

              {currentStep.type === 'form' && (
                <div className="max-w-[90%] mx-auto space-y-4">
                  {currentStep.fields?.map((field) => (
                    <div key={field.name} className="space-y-1">
                      <label className="text-sm font-semibold text-gray-700 ml-1">
                        {field.label} {field.required && <span className="text-red-500">*</span>}
                      </label>
                      {field.type === 'textarea' ? (
                        <textarea
                          value={customerInfo[field.name] || ''}
                          onChange={(e) =>
                            setCustomerInfo({ ...customerInfo, [field.name]: e.target.value })
                          }
                          className="w-full p-4 bg-gray-50 border-2 border-transparent focus:border-[#57c858] focus:bg-white rounded-xl outline-none transition-all min-h-[120px]"
                          placeholder={`Enter your ${field.label.toLowerCase()}...`}
                        />
                      ) : (
                        <input
                          type={field.type}
                          value={customerInfo[field.name] || ''}
                          onChange={(e) =>
                            setCustomerInfo({ ...customerInfo, [field.name]: e.target.value })
                          }
                          className="w-full p-4 bg-gray-50 border-2 border-transparent focus:border-[#57c858] focus:bg-white rounded-xl outline-none transition-all"
                          placeholder={`Enter your ${field.label.toLowerCase()}...`}
                          required={field.required}
                        />
                      )}
                    </div>
                  ))}
                </div>
              )}

              {currentStep.type === 'summary' && (
                <SummarySection
                  flow={flow}
                  cart={cart}
                  selections={selections}
                  customerInfo={customerInfo}
                  updateQuantity={updateQuantity}
                  removeItem={removeItem}
                  handlePricingImport={handlePricingImport}
                  checkDependencies={checkDependencies}
                  logos={logos}
                  error={error}
                />
              )}
            </div>
          </motion.div>
        </AnimatePresence>
      </main>

      {/* Live Price Summary Bar — shown on option steps + the form/summary
          steps (where the submit button lives). Keyed on step.type, not
          literal ids; identical to the legacy condition for the steps the
          configurator actually visits (Spring/Office option steps). */}
      <AnimatePresence>
        {(isOptionsStep(currentStep) ||
          currentStep.type === 'summary' ||
          currentStep.type === 'form') && (
          <motion.div
            key="sticky-price-bar"
            initial={{ y: 100 }}
            animate={{ y: 0 }}
            exit={{ y: 100 }}
            className="fixed bottom-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-lg border-t border-gray-100 shadow-[0_-10px_30px_rgba(0,0,0,0.05)] py-4 px-6"
          >
            <div className="max-w-[90%] mx-auto flex flex-wrap items-center justify-between gap-x-6 gap-y-3">
              {/* Redesign pass B (item 7) — quantity is set HERE, from step
                  one, and only here. Per-box price AND the running total are
                  always visible so the price has context before checkout. */}
              <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
                <QuantityStepper
                  qty={cart.qty}
                  floor={defaults.minQuantity}
                  onChange={updateQuantity}
                  inputRef={qtyInputRef}
                  /* Chapter 10 (F4) — the band hint's saving is computed from
                     the REAL resolved prices for the current selections, so a
                     flat-priced box (Office) advertises no discount. */
                  unitPriceAt={(atQty) =>
                    computeUnitBoxPrice(flow, selections, atQty)
                  }
                />
                <div className="flex flex-col">
                  <div className="flex items-baseline gap-1.5">
                    <span className="text-xl md:text-2xl font-black text-[#444444]">
                      {formatMoney(cart.pricing.unit, cart.pricing.currency)}
                    </span>
                    <span className="text-[13px] font-bold text-gray-400">/ box</span>
                  </div>
                  <span className="text-[13px] font-bold text-[#356d4e]">
                    {formatMoney(cart.pricing.total, cart.pricing.currency)} total ({cart.qty}{' '}
                    boxes) · excl. VAT
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <button
                  onClick={() => setIsCartOpen(true)}
                  className="hidden md:flex items-center gap-2 px-6 py-3 rounded-full border border-gray-200 text-sm font-bold text-[#444444] hover:bg-gray-50 transition-all"
                >
                  <CartIcon className="w-4 h-4" />
                  View Details
                </button>
                {currentStep.type === 'summary' ? (
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => void handleSubmit(true)}
                    disabled={isSubmitting}
                    className="px-10 py-4 bg-linear-to-r from-[#356d4e] to-[#57c858] text-white rounded-full font-black text-sm uppercase tracking-widest shadow-lg shadow-[#356d4e]/20 disabled:opacity-50 disabled:grayscale transition-all flex items-center gap-2"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        Submit Request
                        <ChevronRight className="w-4 h-4" />
                      </>
                    )}
                  </motion.button>
                ) : (
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={nextStep}
                    disabled={!isStepValid()}
                    className="px-10 py-4 bg-linear-to-r from-[#356d4e] to-[#57c858] text-white rounded-full font-black text-sm uppercase tracking-widest shadow-lg shadow-[#356d4e]/20 disabled:opacity-50 disabled:grayscale transition-all flex items-center gap-2"
                  >
                    {isLastOptionStep ? 'Review Quote' : 'Next Step'}
                    <ChevronRight className="w-4 h-4" />
                  </motion.button>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      {/* Redesign pass C (item 16) — the shared footer replaces the old
          lone privacy link (the privacy modal now lives in the footer).
          Extra bottom padding keeps the legal bar clear of the fixed
          price bar, which wraps taller on narrow screens. */}
      <SiteFooter className="pb-44 sm:pb-32" />
    </div>
  );
}

interface SummaryProps {
  flow: FlowConfig;
  cart: Cart;
  selections: Selections;
  customerInfo: CustomerInfo;
  updateQuantity: (q: number) => void;
  removeItem: (id: string) => void;
  handlePricingImport: (e: React.ChangeEvent<HTMLInputElement>) => void;
  checkDependencies: (step: Step) => boolean;
  logos: readonly LogoSelection[];
  error: string | null;
}

function SummarySection(props: SummaryProps) {
  const {
    flow,
    cart,
    selections,
    customerInfo,
    updateQuantity,
    removeItem,
    handlePricingImport,
    checkDependencies,
    logos,
    error,
  } = props;

  return (
    <div className="space-y-10">
      {/* The band is announced by the stepper's own hint (which is on this
          screen too), so this region no longer repeats it — it carries the one
          thing the stepper does not say: the running total. */}
      <div className="sr-only" aria-live="polite">
        {`Current quantity: ${cart.qty}. Total price: ${formatMoney(cart.pricing.total, cart.pricing.currency)}.`}
      </div>

      <div className="bg-white p-8 rounded-[32px] shadow-sm border border-gray-100 space-y-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="space-y-1">
            <h3 className="text-xl font-black text-[#356d4e] uppercase tracking-tight flex items-center gap-2">
              <ShoppingBag className="w-5 h-5 text-[#57c858]" />
              Quantity of boxes
            </h3>
            <p className="text-xs font-bold text-gray-400">
              Prices and availability depend on number of boxes.
            </p>
          </div>
          {(() => {
            // Chapter 10 (F4): the STORED key `250+` denotes 250-499 — showing
            // it raw told the customer 250 and 2,500 were one band.
            const tier = getActiveTier(cart.qty);
            return tier === null ? null : (
              <div className="bg-[#57c858]/10 px-4 py-2 rounded-xl border border-[#57c858]/20">
                <span className="text-xs font-black text-[#356d4e] uppercase tracking-widest">
                  Active Tier: <span className="text-[#57c858]">{describeBand(tier)}</span>
                </span>
              </div>
            );
          })()}
        </div>

        {/* Redesign pass B (item 7) — read-only mirror. Quantity is SET in
            the sticky price bar's stepper (visible right below this card);
            the summary only reflects it so there's one setting surface. */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-8">
          <div className="flex items-baseline gap-2">
            <span className="text-4xl font-black text-[#356d4e]">{cart.qty}</span>
            <span className="text-sm font-bold text-gray-400">
              boxes
            </span>
          </div>
          <p className="text-sm font-bold text-[#444444] opacity-70 max-w-md">
            Adjust the quantity with the − / + stepper in the price bar below. Prices and
            availability update automatically.
          </p>
        </div>
      </div>

      {(() => {
        const unavailableItems: ({
          id: string;
          label: string;
          minBoxes?: number;
          stepId: string;
        })[] = [];
        flow.steps.forEach((step) => {
          step.options?.forEach((opt) => {
            if (
              (selections[step.id] || []).includes(opt.id) &&
              opt.minBoxes &&
              cart.qty < opt.minBoxes
            ) {
              unavailableItems.push({
                id: opt.id,
                label: opt.label,
                minBoxes: opt.minBoxes,
                stepId: step.id,
              });
            }
          });
        });

        if (unavailableItems.length > 0) {
          return (
            <div className="p-6 bg-red-50 border border-red-100 rounded-[32px] space-y-4">
              <div className="flex items-center gap-3 text-red-600">
                <AlertCircle className="w-6 h-6" />
                <h4 className="font-black uppercase tracking-widest text-sm">
                  Action Required: Unavailable Items
                </h4>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {unavailableItems.map((item) => (
                  <div
                    key={item.id}
                    className="bg-white p-4 rounded-2xl shadow-sm border border-red-100 flex flex-col gap-3"
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-black text-[#356d4e] text-sm">{item.label}</p>
                        <p className="text-[13px] font-bold text-red-500 mt-1">
                          Unavailable at {cart.qty} (minimum {item.minBoxes})
                        </p>
                      </div>
                      <button
                        onClick={() => removeItem(item.id)}
                        className="text-gray-400 hover:text-red-500 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          updateQuantity(item.minBoxes ?? 0);
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }}
                        className="flex-1 py-2 bg-[#356d4e] text-white text-xs font-black uppercase tracking-widest rounded-lg hover:brightness-110 transition-all"
                      >
                        Increase to {item.minBoxes}
                      </button>
                      <button
                        onClick={() => removeItem(item.id)}
                        className="px-4 py-2 border border-red-200 text-red-500 text-xs font-black uppercase tracking-widest rounded-lg hover:bg-red-50 transition-all"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        }
        return null;
      })()}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2 space-y-6">
          <div className="flex items-center justify-between">
            <h3 className="text-xl font-black text-[#356d4e] uppercase tracking-tight flex items-center gap-2">
              <Package className="w-5 h-5 text-[#57c858]" />
              Selected Items
            </h3>
            <span className="text-[13px] font-bold text-gray-500 uppercase tracking-widest">
              {Object.values(selections).flat().length} Items
            </span>
          </div>

          <div className="bg-white rounded-[32px] shadow-sm border border-gray-100 overflow-hidden">
            <div className="divide-y divide-gray-50">
              {flow.steps
                .filter((s) => (!s.type || s.type === 'options') && checkDependencies(s))
                // N2-closeout — flatMap into ONE keyed list. The previous
                // `.map(step => selectedIds.map(...))` returned an array of
                // arrays; React rendered each inner array as an unkeyed child,
                // emitting "two children with the same key, ``" warnings. Each
                // row is now keyed by `${step.id}:${option.id}` (unique across
                // steps, since the same option id can repeat in two steps).
                .flatMap((step) => {
                  const selectedIds = selections[step.id] || [];
                  return selectedIds.map((id) => {
                    const option = step.options?.find((o) => o.id === id);
                    if (!option) return null;
                    const isUnavailable = !!(
                      option.minBoxes && cart.qty < option.minBoxes
                    );
                    const pricePerBox = isUnavailable ? 0 : getTieredPrice(option, cart.qty);
                    const lineTotal = pricePerBox * cart.qty;

                    return (
                      <div
                        key={`${step.id}:${option.id}`}
                        className={`p-4 flex items-center gap-4 transition-colors ${
                          isUnavailable ? 'bg-red-50/30' : 'hover:bg-gray-50/50'
                        }`}
                      >
                        <div className="w-16 h-16 rounded-xl overflow-hidden bg-gray-100 flex-shrink-0 border border-gray-100">
                          <img
                            src={option.image}
                            alt={option.label}
                            className={`w-full h-full object-cover ${
                              isUnavailable ? 'grayscale opacity-50' : ''
                            }`}
                          />
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2">
                            <h4
                              className={`font-black text-sm uppercase tracking-tight truncate ${
                                isUnavailable ? 'text-gray-400' : 'text-[#356d4e]'
                              }`}
                            >
                              {option.label}
                            </h4>
                            <div className="text-right flex-shrink-0">
                              <p
                                className={`font-black text-sm ${
                                  isUnavailable ? 'text-gray-300 line-through' : 'text-[#356d4e]'
                                }`}
                              >
                                {formatMoney(lineTotal, cart.pricing.currency)}
                              </p>
                            </div>
                          </div>

                          <div className="flex items-center justify-between mt-1">
                            <div className="flex items-center gap-3">
                              <p className="text-sm font-bold text-gray-500">
                                {isUnavailable
                                  ? 'Unavailable'
                                  : `${formatMoney(pricePerBox, cart.pricing.currency)} / box`}
                              </p>
                              <button
                                onClick={() => removeItem(option.id)}
                                className="text-gray-300 hover:text-red-500 transition-colors p-1"
                              >
                                <Trash2 className="w-3 h-3" />
                              </button>
                            </div>
                            <p className="text-[13px] font-bold text-gray-500">
                              Qty: {cart.qty}
                            </p>
                          </div>

                          {isUnavailable && (
                            <p className="text-[13px] font-black text-red-500 mt-2 flex items-center gap-1">
                              <AlertCircle className="w-3 h-3" />
                              Min. {option.minBoxes} boxes required
                            </p>
                          )}
                        </div>
                      </div>
                    );
                  });
                })}
            </div>
          </div>

          {logos.length > 0 && (
            <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-100 flex items-center gap-4">
              <div className="w-12 h-12 bg-gray-50 rounded-xl flex items-center justify-center border border-gray-100">
                <ImageIcon className="w-6 h-6 text-[#57c858]" />
              </div>
              <div>
                <p className="text-[11px] font-bold text-gray-400 uppercase tracking-widest">
                  {/* F15 — plural when a company attached several marks. */}
                  {logos.length > 1 ? 'Branding Logos' : 'Branding Logo'}
                </p>
                <p className="font-black text-[#356d4e] text-sm">
                  {logos.map((l) => l.file.name).join(', ')}
                </p>
              </div>
            </div>
          )}
        </div>

        <div className="space-y-8">
          <div className="bg-[#356d4e] p-8 rounded-[40px] text-white shadow-2xl shadow-[#356d4e]/20 space-y-6 sticky top-8">
            <div className="space-y-2">
              <p className="text-[11px] font-black uppercase tracking-[0.2em] text-white/60">
                Estimated Total
              </p>
              <div className="flex items-baseline gap-1">
                <span className="text-4xl font-black">
                  {formatMoney(cart.pricing.total, cart.pricing.currency)}
                </span>
                <span className="text-sm font-bold text-white/60">
                  excl. VAT
                </span>
              </div>
            </div>

            <div className="space-y-4 pt-6 border-t border-white/10">
              <div className="flex justify-between items-center">
                <span className="text-[11px] font-black uppercase tracking-widest text-white/60">
                  Price per box
                </span>
                <span className="font-black">
                  {formatMoney(cart.pricing.unit, cart.pricing.currency)}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-[11px] font-black uppercase tracking-widest text-white/60">
                  Quantity
                </span>
                <span className="font-black">{cart.qty} boxes</span>
              </div>
            </div>

            <div className="pt-4">
              <p className="text-xs font-bold text-white/50 italic leading-relaxed">
                * Final pricing will be confirmed after review of your branding requirements and
                delivery details.
              </p>
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="text-sm font-black text-[#356d4e] uppercase tracking-widest flex items-center gap-2 px-2">
              <User className="w-4 h-4 text-[#57c858]" />
              Contact Details
            </h3>
            <div className="bg-white p-6 rounded-[32px] shadow-sm border border-gray-100 space-y-4">
              {flow.steps
                .find((s) => s.type === 'form')
                ?.fields?.map((field) => (
                  <div key={field.name} className="flex justify-between items-start gap-4">
                    <span className="text-[11px] font-black text-gray-400 uppercase tracking-widest">
                      {field.label}
                    </span>
                    <span className="text-[13px] font-bold text-[#356d4e] text-right truncate max-w-[150px]">
                      {customerInfo[field.name] || '-'}
                    </span>
                  </div>
                ))}
            </div>
          </div>

          <div className="px-4">
            <label className="flex items-center gap-2 text-gray-400 hover:text-[#356d4e] cursor-pointer transition-colors group">
              <Upload className="w-4 h-4 group-hover:scale-110 transition-transform" />
              <span className="text-xs font-black uppercase tracking-widest">
                Import Pricing CSV
              </span>
              <input
                type="file"
                accept=".csv"
                className="hidden"
                onChange={handlePricingImport}
              />
            </label>
          </div>
        </div>
      </div>

      {error && (
        <div className="p-6 bg-red-50 text-red-700 rounded-[32px] border border-red-100 flex items-center gap-3 shadow-sm">
          <AlertCircle className="w-6 h-6" />
          <p className="font-bold text-sm">{error}</p>
        </div>
      )}
    </div>
  );
}
